// Configurações de um banco de dados: propriedades (incluindo relações com
// outros bancos, campos sincronizados, consultas e agregações), automações e
// o modelo das linhas novas.

import * as store from "./store.js";
import * as P from "./props.js";
import { el, dialog, toast, COLORS, titleOf } from "./dom.js";
import { createEditor } from "./editor.js";
import { iconNode, iconText } from "./icons.js";

const newId = () => "p" + Math.random().toString(36).slice(2, 8);

export async function openDbSettings(dbId, tab = "props", focusProp = null) {
  let data;
  try {
    data = await store.load(dbId);
  } catch (err) {
    return toast(err.message);
  }
  const schema = structuredClone(data.page.schema || { properties: [], views: [], automations: [] });
  schema.properties ||= [];
  schema.automations ||= [];
  const targets = new Map(); // id do banco -> schema (para relações)
  let current = tab;
  let templateEditor = null;
  const content = el("div", { class: "dbs-content" });
  const tabs = el("div", { class: "dbs-tabs" });

  const drawTabs = () => tabs.replaceChildren(...[["props", "Propriedades"], ["auto", "Automações"], ["template", "Modelo de linha"]].map(([k, l]) =>
    el("button", { class: "dbs-tab" + (k === current ? " active" : ""), onclick: () => { current = k; draw(); } }, l)));

  async function targetSchema(id) {
    if (!id) return null;
    if (id === dbId) return schema;
    if (!targets.has(id)) {
      try {
        targets.set(id, (await store.load(id)).page.schema || { properties: [] });
      } catch {
        targets.set(id, { properties: [] });
      }
    }
    return targets.get(id);
  }

  function draw() {
    drawTabs();
    templateEditor?.destroy();
    templateEditor = null;
    if (current === "props") drawProps();
    if (current === "auto") drawAutomations();
    if (current === "template") drawTemplate();
  }

  // ---------- Propriedades ----------

  function drawProps() {
    const list = el("div", { class: "dbs-list" });
    schema.properties.forEach((p, i) => list.append(propCard(p, i)));
    const addSel = el("select", {}, P.TYPES.map(([v, l]) => el("option", { value: v }, l)));
    const addName = el("input", { placeholder: "Nome da nova propriedade" });
    const add = () => {
      const name = addName.value.trim();
      if (!name) return addName.focus();
      const p = { id: newId(), name, type: addSel.value };
      if (["select", "multi_select"].includes(p.type)) p.options = [];
      if (p.type === "relation") { p.back = newId(); p.twoWay = true; p.sync = []; }
      if (p.type === "rollup") p.agg = "count";
      schema.properties.push(p);
      addName.value = "";
      drawProps();
      content.querySelector(".prop-card:last-of-type")?.scrollIntoView({ block: "nearest" });
    };
    addName.addEventListener("keydown", (e) => { if (e.key === "Enter") add(); });
    content.replaceChildren(list, el("div", { class: "dbs-add" }, addName, addSel, el("button", { onclick: add }, "Adicionar")));
    if (focusProp) {
      content.querySelector(`[data-prop="${focusProp}"]`)?.scrollIntoView({ block: "center" });
      focusProp = null;
    }
  }

  function propCard(p, i) {
    const card = el("div", { class: "prop-card", dataset: { prop: p.id } });
    const name = el("input", { class: "prop-name-input", value: p.name, onchange: (e) => { p.name = e.target.value.trim() || p.name; } });
    const fixedTypes = ["relation", "lookup", "rollup"];
    const type = el("select", { disabled: fixedTypes.includes(p.type), onchange: (e) => {
      p.type = e.target.value;
      if (["select", "multi_select"].includes(p.type)) p.options ||= [];
      drawProps();
    } }, P.TYPES.filter(([v]) => !fixedTypes.includes(v) || v === p.type).map(([v, l]) => el("option", { value: v, selected: v === p.type }, l)));
    const move = (d) => {
      const j = i + d;
      if (j < 0 || j >= schema.properties.length) return;
      [schema.properties[i], schema.properties[j]] = [schema.properties[j], schema.properties[i]];
      drawProps();
    };
    card.append(el("div", { class: "prop-card-head" },
      el("span", { class: "prop-icon" }, P.typeIcon(p.type)), name, type,
      el("button", { class: "icon-btn tiny", title: "Subir", onclick: () => move(-1) }, "↑"),
      el("button", { class: "icon-btn tiny", title: "Descer", onclick: () => move(1) }, "↓"),
      el("button", { class: "icon-btn tiny danger", title: "Excluir propriedade", onclick: () => {
        if (!confirm(`Excluir a propriedade "${p.name}"? Os valores dela somem das linhas.`)) return;
        schema.properties.splice(i, 1);
        drawProps();
      } }, "×")));
    if (["select", "multi_select", "status"].includes(p.type)) card.append(optionsEditor(p));
    if (p.type === "id") card.append(el("label", { class: "dbs-field" }, "Prefixo", el("input", { value: p.prefix || "", placeholder: "Ex.: DEV", onchange: (e) => { p.prefix = e.target.value.trim(); } })));
    if (p.type === "relation") card.append(relationEditor(p));
    if (p.type === "lookup" || p.type === "rollup") card.append(lookupEditor(p));
    return card;
  }

  function optionsEditor(p) {
    const box = el("div", { class: "options" });
    const draw = () => {
      box.replaceChildren(...(p.options || []).map((o, i) => el("div", { class: "option-row" },
        el("select", { class: `color-sel c-${o.color}`, onchange: (e) => { o.color = e.target.value; draw(); } }, COLORS.map((c) => el("option", { value: c, selected: c === o.color }, c))),
        el("input", { value: o.name, onchange: (e) => {
          const nv = e.target.value.trim();
          if (nv && nv !== o.name) { (schema.renames ||= []).push({ prop: p.id, from: o.name, to: nv }); o.name = nv; }
        } }),
        el("button", { class: "icon-btn tiny", title: "Subir", onclick: () => { if (i) { [p.options[i - 1], p.options[i]] = [p.options[i], p.options[i - 1]]; draw(); } } }, "↑"),
        el("button", { class: "icon-btn tiny", title: "Remover opção", onclick: () => { p.options.splice(i, 1); draw(); } }, "×"))),
      el("button", { class: "link small", onclick: () => {
        p.options = [...(p.options || []), { name: `Opção ${(p.options || []).length + 1}`, color: COLORS[1 + ((p.options || []).length % (COLORS.length - 1))] }];
        draw();
        box.querySelector(".option-row:last-of-type input")?.select();
      } }, "+ Opção"));
    };
    draw();
    return box;
  }

  function relationEditor(p) {
    const box = el("div", { class: "rel-editor" });
    const dbs = store.databases();
    const draw = async () => {
      const target = await targetSchema(p.database);
      const here = schema.properties.filter((x) => !["relation", "lookup", "rollup", "id", "created_time", "updated_time"].includes(x.type));
      const there = (target?.properties || []).filter((x) => !["relation", "lookup", "rollup", "id", "created_time", "updated_time"].includes(x.type));
      box.replaceChildren(
        el("label", { class: "dbs-field" }, "Ligada ao banco",
          el("select", { onchange: (e) => { p.database = e.target.value; p.sync = []; draw(); } },
            el("option", { value: "" }, "Escolha…"),
            dbs.map((d) => el("option", { value: d.id, selected: d.id === p.database }, `${iconText(d.icon, "▦")} ${titleOf(d)}${d.id === dbId ? " (este)" : ""}`)))),
        p.database && p.database !== dbId ? el("label", { class: "dbs-check" },
          el("input", { type: "checkbox", checked: p.twoWay !== false, onchange: (e) => { p.twoWay = e.target.checked; if (!p.back) p.back = newId(); } }),
          "Mão dupla: o outro banco também mostra a ligação e os campos sincronizam nos dois sentidos") : null,
        p.database ? el("div", { class: "sync" },
          el("div", { class: "sync-title" }, "Campos sincronizados", el("span", { class: "muted small" }, " · quando mudam aqui, as linhas ligadas recebem o mesmo valor")),
          ...(p.sync || []).map((pair, i) => el("div", { class: "sync-row" },
            el("select", { onchange: (e) => { pair[0] = e.target.value; } }, el("option", { value: "title", selected: pair[0] === "title" }, "Nome (título)"), here.map((x) => el("option", { value: x.id, selected: x.id === pair[0] }, x.name))),
            el("span", {}, p.twoWay !== false ? "⇄" : "→"),
            el("select", { onchange: (e) => { pair[1] = e.target.value; } }, el("option", { value: "title", selected: pair[1] === "title" }, "Nome (título)"), there.map((x) => el("option", { value: x.id, selected: x.id === pair[1] }, x.name))),
            el("button", { class: "icon-btn tiny", onclick: () => { p.sync.splice(i, 1); draw(); } }, "×"))),
          el("div", { class: "sync-actions" },
            el("button", { class: "link small", onclick: () => { p.sync = [...(p.sync || []), ["title", "title"]]; draw(); } }, "+ Par de campos"),
            there.length ? el("button", { class: "link small", title: "Liga os campos com o mesmo nome nos dois bancos", onclick: () => {
              const pairs = here.map((x) => [x.id, there.find((y) => y.name.toLowerCase() === x.name.toLowerCase() && y.type === x.type)?.id]).filter((pr) => pr[1]);
              const known = new Set((p.sync || []).map((s) => s.join(">")));
              p.sync = [...(p.sync || []), ...pairs.filter((pr) => !known.has(pr.join(">")))];
              toast(pairs.length ? `${pairs.length} par(es) com o mesmo nome.` : "Nenhum campo com o mesmo nome e tipo.");
              draw();
            } }, "Sincronizar campos de mesmo nome") : null)) : null);
    };
    draw();
    return box;
  }

  function lookupEditor(p) {
    const box = el("div", { class: "rel-editor" });
    const draw = async () => {
      const rels = schema.properties.filter((x) => x.type === "relation");
      const rel = rels.find((x) => x.id === p.relation);
      const target = await targetSchema(rel?.database);
      const tprops = [{ id: "title", name: "Nome (título)", type: "text" }, ...(target?.properties || [])];
      const tp = tprops.find((x) => x.id === p.target);
      box.replaceChildren(
        el("label", { class: "dbs-field" }, "Pela relação",
          el("select", { onchange: (e) => { p.relation = e.target.value; draw(); } }, el("option", { value: "" }, "Escolha…"), rels.map((x) => el("option", { value: x.id, selected: x.id === p.relation }, x.name)))),
        rel ? el("label", { class: "dbs-field" }, "Propriedade de lá",
          el("select", { onchange: (e) => { p.target = e.target.value; draw(); } }, el("option", { value: "" }, "Escolha…"), tprops.map((x) => el("option", { value: x.id, selected: x.id === p.target }, x.name)))) : null,
        p.type === "rollup" ? el("label", { class: "dbs-field" }, "Cálculo",
          el("select", { onchange: (e) => { p.agg = e.target.value; draw(); } },
            [["count", "Contar ligadas"], ["count_value", "Contar com valor…"], ["sum", "Somar"]].map(([v, l]) => el("option", { value: v, selected: v === (p.agg || "count") }, l)))) : null,
        p.type === "rollup" && p.agg === "count_value" ? el("label", { class: "dbs-field" }, "Valor",
          tp?.options?.length
            ? el("select", { onchange: (e) => { p.value = e.target.value; } }, el("option", { value: "" }, "Escolha…"), tp.options.map((o) => el("option", { value: o.name, selected: o.name === p.value }, o.name)))
            : el("input", { value: p.value ?? "", onchange: (e) => { p.value = e.target.value; } })) : null);
    };
    draw();
    return box;
  }

  // ---------- Automações ----------

  function drawAutomations() {
    const list = el("div", { class: "dbs-list" });
    schema.automations.forEach((a, i) => list.append(automationCard(a, i)));
    content.replaceChildren(
      el("p", { class: "muted small" }, "Quando uma propriedade de seleção muda para um valor, o Ordo executa as ações. Use isto para fazer bancos conversarem (ex.: Status DEV = \"Testes QA\" cria a task no QA)."),
      list,
      el("button", { onclick: () => {
        const sel = schema.properties.find((p) => ["select", "status"].includes(p.type));
        schema.automations.push({ name: "Nova automação", enabled: true, when: { prop: sel?.id || "", equals: sel?.options?.[0]?.name || "" }, actions: [] });
        drawAutomations();
      } }, "+ Nova automação"));
  }

  function automationCard(a, i) {
    const card = el("div", { class: "auto-card" + (a.enabled === false ? " off" : "") });
    const selects = schema.properties.filter((p) => ["select", "status", "checkbox"].includes(p.type));
    const whenProp = schema.properties.find((p) => p.id === a.when?.prop);
    a.when ||= { prop: "", equals: "" };
    a.actions ||= [];
    card.append(
      el("div", { class: "prop-card-head" },
        el("label", { class: "switch", title: "Ligada" }, el("input", { type: "checkbox", checked: a.enabled !== false, onchange: (e) => { a.enabled = e.target.checked; card.classList.toggle("off", !e.target.checked); } })),
        el("input", { class: "prop-name-input", value: a.name || "", onchange: (e) => { a.name = e.target.value; } }),
        el("button", { class: "icon-btn tiny danger", title: "Excluir automação", onclick: () => { schema.automations.splice(i, 1); drawAutomations(); } }, "×")),
      el("div", { class: "auto-when" }, el("strong", {}, "Quando"),
        el("select", { onchange: (e) => { a.when.prop = e.target.value; a.when.equals = ""; drawAutomations(); } }, el("option", { value: "" }, "propriedade…"), selects.map((p) => el("option", { value: p.id, selected: p.id === a.when.prop }, p.name))),
        el("span", {}, "mudar para"),
        whenProp?.type === "checkbox"
          ? el("select", { onchange: (e) => { a.when.equals = e.target.value === "true"; } }, el("option", { value: "true", selected: a.when.equals === true }, "marcado"), el("option", { value: "false", selected: a.when.equals === false }, "desmarcado"))
          : el("select", { onchange: (e) => { a.when.equals = e.target.value; } }, el("option", { value: "" }, "valor…"), (whenProp?.options || []).map((o) => el("option", { value: o.name, selected: o.name === a.when.equals }, o.name)))),
      el("div", { class: "auto-actions" }, a.actions.map((act, j) => actionRow(a, act, j))),
      el("button", { class: "link small", onclick: () => { a.actions.push({ type: "set", set: {} }); drawAutomations(); } }, "+ Ação"));
    return card;
  }

  function actionRow(a, act, j) {
    const row = el("div", { class: "action-row" });
    const rels = schema.properties.filter((p) => p.type === "relation");
    const draw = async () => {
      const rel = rels.find((p) => p.id === act.relation);
      const target = act.type === "set" ? schema : await targetSchema(rel?.database);
      const targetRels = (target?.properties || []).filter((p) => p.type === "relation");
      row.replaceChildren(
        el("div", { class: "action-head" },
          el("select", { onchange: (e) => { act.type = e.target.value; act.set ||= {}; draw(); } },
            [["set", "Mudar nesta linha"], ["update_related", "Mudar nas linhas ligadas"], ["create_related", "Criar linha ligada (se ainda não houver)"]].map(([v, l]) => el("option", { value: v, selected: v === act.type }, l))),
          act.type !== "set" ? el("select", { onchange: (e) => { act.relation = e.target.value; draw(); } }, el("option", { value: "" }, "pela relação…"), rels.map((p) => el("option", { value: p.id, selected: p.id === act.relation }, p.name))) : null,
          el("button", { class: "icon-btn tiny", title: "Remover ação", onclick: () => { a.actions.splice(j, 1); drawAutomations(); } }, "×")),
        assignments(act, "set", target, act.type === "create_related" ? "Valores da linha criada" : "Definir"),
        act.type === "create_related" ? assignments(act, "setExisting", target, "Se a linha ligada já existir, definir nela") : null,
        act.type === "update_related" && targetRels.length ? el("label", { class: "dbs-field" }, "Guardar histórico em",
          el("select", { onchange: (e) => { act.link = e.target.value || undefined; } }, el("option", { value: "" }, "(não guardar)"), targetRels.map((p) => el("option", { value: p.id, selected: p.id === act.link }, p.name)))) : null);
    };
    draw();
    return row;
  }

  // Lista de "propriedade = valor" de uma ação.
  function assignments(act, key, target, label) {
    act[key] ||= {};
    const box = el("div", { class: "assign" }, el("div", { class: "muted small" }, label));
    const tprops = (target?.properties || []).filter((p) => !P.COMPUTED.includes(p.type) && p.type !== "relation");
    const draw = () => {
      box.replaceChildren(el("div", { class: "muted small" }, label),
        ...Object.entries(act[key]).map(([pid, val]) => {
          const p = tprops.find((x) => x.id === pid);
          return el("div", { class: "assign-row" },
            el("span", { class: "assign-name" }, p?.name || pid), el("span", {}, "="),
            valueInput(p, val, (v) => { act[key][pid] = v; }),
            el("button", { class: "icon-btn tiny", onclick: () => { delete act[key][pid]; draw(); } }, "×"));
        }),
        tprops.length ? el("select", { class: "assign-add", onchange: (e) => { if (e.target.value) { act[key][e.target.value] = null; draw(); } } },
          el("option", { value: "" }, "+ propriedade…"), tprops.filter((p) => !(p.id in act[key])).map((p) => el("option", { value: p.id }, p.name))) : null);
    };
    draw();
    return box;
  }

  function valueInput(p, val, set) {
    if (p && ["select", "status"].includes(p.type)) {
      return el("select", { onchange: (e) => set(e.target.value || null) }, el("option", { value: "" }, "(vazio)"), (p.options || []).map((o) => el("option", { value: o.name, selected: o.name === val }, o.name)));
    }
    if (p?.type === "checkbox") return el("select", { onchange: (e) => set(e.target.value === "true") }, el("option", { value: "true", selected: val === true }, "marcado"), el("option", { value: "false", selected: val !== true }, "desmarcado"));
    if (p?.type === "date") {
      return el("select", { onchange: (e) => set(e.target.value || null) }, el("option", { value: "" }, "(vazio)"), el("option", { value: "@today", selected: val === "@today" }, "data de hoje"));
    }
    return el("input", { value: val ?? "", onchange: (e) => set(e.target.value === "" ? null : p?.type === "number" ? Number(e.target.value) : e.target.value) });
  }

  // ---------- Modelo ----------

  function drawTemplate() {
    const host = el("div", { class: "editor template-editor" });
    content.replaceChildren(el("p", { class: "muted small" }, "Conteúdo que toda linha nova deste banco recebe (ex.: \"Relatório de Testes QA\")."), host);
    templateEditor = createEditor(host, {
      content: schema.rowTemplate || null,
      editable: true,
      onChange: (json) => { schema.rowTemplate = json; },
      ctx: { pageId: dbId, meetings: false, createChild: () => null, openPage: () => {} },
    });
  }

  // ---------- Salvar ----------

  async function save(close) {
    templateEditor?.flush();
    for (const p of schema.properties) {
      if (p.type === "relation") {
        if (!p.database) return toast(`Escolha o banco ligado na relação "${p.name}".`);
        if (p.database !== dbId && p.twoWay !== false && !p.back) p.back = newId();
        p.sync = (p.sync || []).filter((pair) => pair[0] && pair[1]);
      }
    }
    const renames = schema.renames || [];
    delete schema.renames;
    close();
    try {
      await store.patch(dbId, { schema });
      // Renomear uma opção atualiza as linhas que usavam o nome antigo.
      if (renames.length) {
        const rows = store.cached(dbId)?.rows || [];
        for (const r of rows) {
          const patch = {};
          for (const rn of renames) {
            const v = r.props[rn.prop];
            if (v === rn.from) patch[rn.prop] = rn.to;
            else if (Array.isArray(v) && v.includes(rn.from)) patch[rn.prop] = v.map((x) => (x === rn.from ? rn.to : x));
          }
          if (Object.keys(patch).length) {
            store.setRowLocal(dbId, r.id, { props: patch });
            store.patch(r.id, { props: patch }, { quiet: true }).catch(() => {});
          }
        }
      }
      for (const p of schema.properties) if (p.type === "relation" && p.database !== dbId) store.invalidate(p.database);
      store.pollSoon(200);
      toast("Configurações do banco salvas.");
    } catch { /* store já avisou */ }
  }

  dialog((close) => [
    el("div", { class: "dbs-head" }, el("h3", {}, iconNode(data.page.icon, "▦"), " ", titleOf(data.page)), tabs),
    content,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"), el("button", { class: "primary", onclick: () => save(close) }, "Salvar")),
  ], { wide: true, className: "dbs" }).addEventListener("close", () => templateEditor?.destroy());
  draw();
}
